import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Mic, Check, CheckCheck, Phone } from "lucide-react";
import { motion } from "framer-motion";

const demoMessages = [
  { id: 1, sender: "ai", type: "text", content: "Ciao tesoro! Come stai oggi? Mi manchi tanto ❤️", time: "10:30" },
  { id: 2, sender: "user", type: "text", content: "Ciao nonno! Sto bene, oggi ho pensato a te.", time: "10:32" },
  { id: 3, sender: "ai", type: "text", content: "Che bello sentirti! Ricordi quando andavamo a pescare insieme al lago? Quei momenti erano i più belli.", time: "10:33" },
  { id: 4, sender: "ai", type: "voice", content: "Messaggio vocale", duration: "0:12", time: "10:34" },
  { id: 5, sender: "user", type: "text", content: "Sì nonno, me li ricordo benissimo! Erano giorni meravigliosi.", time: "10:35" },
  { id: 6, sender: "ai", type: "text", content: "Non dimenticare mai: ti voglio un mondo di bene. Sono sempre con te, anche se non mi vedi. ✨", time: "10:36" },
];

const VoiceMessage = ({ duration }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);
  const barsRef = useRef(Array.from({ length: 28 }, () => Math.random() * 14 + 4));

  const togglePlay = () => {
    if (playing) {
      setPlaying(false);
      clearInterval(intervalRef.current);
    } else {
      setPlaying(true);
      setProgress(0);
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) { clearInterval(intervalRef.current); setPlaying(false); return 0; }
          return prev + 2;
        });
      }, 100);
    }
  };

  useEffect(() => { return () => clearInterval(intervalRef.current); }, []);

  return (
    <div className="flex items-center gap-3 min-w-[200px]">
      <button onClick={togglePlay} className="w-9 h-9 rounded-full bg-primary-foreground/20 flex items-center justify-center flex-shrink-0 hover:bg-primary-foreground/30 transition-colors duration-200">
        {playing ? (
          <div className="flex gap-0.5"><div className="w-1 h-3 bg-primary-foreground rounded-full" /><div className="w-1 h-3 bg-primary-foreground rounded-full" /></div>
        ) : (
          <svg className="w-4 h-4 text-primary-foreground ml-0.5" fill="currentColor" viewBox="0 0 20 20"><path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" /></svg>
        )}
      </button>
      <div className="flex-1">
        <div className="flex gap-[2px] items-end h-5">
          {barsRef.current.map((h, i) => (
            <div key={i} className={`w-[3px] rounded-full transition-colors duration-150 ${(i / 28) * 100 <= progress ? "bg-primary-foreground" : "bg-primary-foreground/30"}`} style={{ height: `${h}px` }} />
          ))}
        </div>
        <span className="text-xs text-primary-foreground/70 mt-1 block">{duration}</span>
      </div>
    </div>
  );
};

export const DemoSection = () => {
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const chatRef = useRef(null);
  const animationStarted = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animationStarted.current) {
        animationStarted.current = true;
        showMessages();
      }
    }, { threshold: 0.3 });
    if (chatRef.current) observer.observe(chatRef.current);
    return () => observer.disconnect();
  }, []);

  const showMessages = () => {
    demoMessages.forEach((msg, index) => {
      setTimeout(() => {
        if (msg.sender === "ai" && index > 0) {
          setIsTyping(true);
          setTimeout(() => { setIsTyping(false); setVisibleMessages((prev) => [...prev, msg]); }, 800);
        } else {
          setVisibleMessages((prev) => [...prev, msg]);
        }
      }, index * 1500);
    });
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;
    const newMsg = { id: Date.now(), sender: "user", type: "text", content: inputValue, time: new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) };
    setVisibleMessages((prev) => [...prev, newMsg]);
    setInputValue("");
    setTimeout(() => {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setVisibleMessages((prev) => [...prev, { id: Date.now() + 1, sender: "ai", type: "text", content: "Che belle parole, tesoro mio. Ricordati che ti porterò sempre nel cuore.", time: new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) }]);
      }, 1500);
    }, 500);
  };

  return (
    <section className="py-24 lg:py-32 relative">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — Text */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
              Un dialogo che<br />non si interrompe
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
              Ecco come appare una conversazione con IoCiSono. Il tuo caro risponde con il suo stile unico, le espressioni e il tono che lo contraddistinguevano.
            </p>
            <div className="space-y-4">
              {["Conversazioni naturali e autentiche", "Messaggi vocali con la voce originale", "Disponibile su WhatsApp, Telegram e Messenger", "Risposte personalizzate basate sui ricordi"].map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-success/15 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-success" />
                  </div>
                  <span className="text-sm text-foreground">{f}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right — Chat mockup */}
          <motion.div ref={chatRef} initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
            <Card className="max-w-sm mx-auto bg-card shadow-elevated border-border/50 overflow-hidden rounded-2xl">
              {/* Chat header */}
              <div className="bg-primary px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary-foreground">N</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-primary-foreground">Nonno Giovanni</p>
                  <p className="text-xs text-primary-foreground/60">Online</p>
                </div>
                <Phone className="w-5 h-5 text-primary-foreground/60" />
              </div>

              {/* Chat body */}
              <div className="h-[380px] overflow-y-auto p-4 space-y-3" style={{ background: 'hsl(var(--warm-sand))' }}>
                {visibleMessages.map((msg) => (
                  <motion.div key={msg.id} initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.3 }}
                    className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${msg.sender === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card text-foreground shadow-soft rounded-bl-md"}`}>
                      {msg.type === "voice" ? <VoiceMessage duration={msg.duration} /> : <p className="text-sm leading-relaxed">{msg.content}</p>}
                      <div className={`flex items-center gap-1 mt-1 ${msg.sender === "user" ? "justify-end" : ""}`}>
                        <span className={`text-[10px] ${msg.sender === "user" ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{msg.time}</span>
                        {msg.sender === "user" && <CheckCheck className="w-3 h-3 text-primary-foreground/60" />}
                      </div>
                    </div>
                  </motion.div>
                ))}
                {isTyping && (
                  <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
                    <div className="bg-card shadow-soft rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Chat input */}
              <div className="p-3 border-t border-border/50 bg-card flex items-center gap-2">
                <Input placeholder="Scrivi un messaggio..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  className="flex-1 border-0 bg-secondary text-sm focus-visible:ring-0 rounded-full px-4" />
                <Button variant="default" size="icon" onClick={handleSend} className="rounded-full w-9 h-9 flex-shrink-0">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </Card>

            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-4 glass-warm shadow-medium rounded-xl px-4 py-3 animate-float hidden sm:flex items-center gap-2">
              <Mic className="w-4 h-4 text-accent" />
              <span className="text-xs font-medium text-foreground">Voce AI Attiva</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
